import React, { useState, useEffect, useMemo } from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, ResponsiveContainer } from 'recharts';
import { getCoordinatesInOrder, getStaticBounds } from '../../bluetooth/sensorMapping';
import './Heatmap.css';

const frontFootImage = `${process.env.PUBLIC_URL}/pointeshoe_front.png`;
const backFootImage = `${process.env.PUBLIC_URL}/pointeshoe_back.png`;

function Heatmap({ pressureData, footSide = 'left' }) {
  const [footView, setFootView] = useState('front');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [displayData, setDisplayData] = useState([]);
  const [maxPressure, setMaxPressure] = useState(800);

  const frontIndices = useMemo(() => [
    ...Array.from({ length: 33 }, (_, i) => i + 31),
    7, 15, 23,
    6, 14, 22, 30,
  ], []);

  const backIndices = useMemo(() => [
    ...Array.from({ length: 32 }, (_, i) => i),
  ], []);

  const getFootImage = (view) => view === 'front' ? frontFootImage : backFootImage;

  const footTitle = footSide.toUpperCase() + ' FOOT';

  const pointCoordinates = useMemo(() => getCoordinatesInOrder(), []);
  const bounds = useMemo(() => getStaticBounds(), []);

  const applyViewTransformation = (coord, view, arrayIndex) => {
    let transformedX = coord.x;
    let transformedY = coord.y;

    if (view === 'front') {
      const frontViewAugmentations = {
        7:  { xMultiplier: 2,   yOffset: -5 },
        15: { xMultiplier: 2,   yOffset: -5 },
        23: { xMultiplier: 2,   yOffset: -5 },
        31: { xMultiplier: 2,   yOffset: -5 },
        6:  { xMultiplier: 2,   yOffset: -5 },
        14: { xMultiplier: 2,   yOffset: -5 },
        22: { xMultiplier: 2,   yOffset: -5 },
        30: { xMultiplier: 2,   yOffset: -5 },
        32: { xMultiplier: 1.2, yOffset: 0 },
        33: { xMultiplier: 1.5, yOffset: 0 },
        34: { xMultiplier: 1.5, yOffset: 0 },
        35: { xMultiplier: 1.5, yOffset: 0 },
        37: { xMultiplier: 1.5, yOffset: 0 },
        38: { xMultiplier: 1.5, yOffset: 0 },
        39: { xMultiplier: 1.2, yOffset: 0 },
      };

      if (frontViewAugmentations.hasOwnProperty(arrayIndex)) {
        const aug = frontViewAugmentations[arrayIndex];
        transformedX *= aug.xMultiplier;
        transformedY += aug.yOffset;
      }

      transformedX = -(transformedX - 1);
      transformedY = transformedY + 2;

    } else if (view === 'back') {
      transformedY = -transformedY + 28;
      transformedX = -transformedX + 3;   // back sensors X center ≈ 0, no offset needed
    }

    return { x: transformedX, y: transformedY };
  };

  const transformedCoordinates = useMemo(() =>
    pointCoordinates.map((coord, index) =>
      applyViewTransformation(coord, footView, index)
    ),
    [footView, pointCoordinates]
  );

  const transformedBounds = useMemo(() => {
    if (footView === 'front') {
      return { xMin: bounds.front.xMin, xMax: bounds.front.xMax, yMin: bounds.front.yMin, yMax: bounds.front.yMax };
    } else {
      return { xMin: bounds.back.xMin,  xMax: bounds.back.xMax,  yMin: bounds.back.yMin,  yMax: bounds.back.yMax };
    }
  }, [footView, bounds]);

  const activeIndices = useMemo(() =>
    footView === 'front' ? frontIndices : backIndices,
    [footView, frontIndices, backIndices]
  );

  const scatterData = useMemo(() => {
    return transformedCoordinates
      .map((coord, arrayIndex) => {
        const sensorIndex = arrayIndex + 1;
        const sensorValue = pressureData[sensorIndex];
        return {
          x: coord.x,
          y: coord.y,
          sensorIndex,
          arrayIndex,
          originalX: pointCoordinates[arrayIndex].x,
          originalY: pointCoordinates[arrayIndex].y,
          value: sensorValue !== undefined ? sensorValue : null,
        };
      })
      .filter(point => activeIndices.includes(point.arrayIndex));
  }, [transformedCoordinates, pressureData, activeIndices, pointCoordinates]);

  useEffect(() => {
    if (!isTransitioning) {
      setDisplayData(prev =>
        JSON.stringify(prev) !== JSON.stringify(scatterData) ? scatterData : prev
      );
    }
  }, [scatterData, isTransitioning]);

  const handleViewChange = async (newView) => {
    if (newView === footView || isTransitioning) return;
    setIsTransitioning(true);
    setDisplayData([]);
    await new Promise(resolve => setTimeout(resolve, 200));
    setFootView(newView);
    await new Promise(resolve => setTimeout(resolve, 100));
    setIsTransitioning(false);
  };

  const handleMaxChange = (e) => {
    const val = parseInt(e.target.value, 10);
    if (!isNaN(val) && val > 0) setMaxPressure(val);
  };

  const getPointColor = (value) => {
    if (value === null || value === undefined) return '#8a8a8aff';
    const t = Math.max(0, Math.min(1, value / maxPressure));

    const colors = [
      { r: 0,   g: 0,   b: 139 },
      { r: 255, g: 0,   b: 255 },
      { r: 255, g: 255, b: 0   },
    ];

    const splitPoint = 0.2;
    let c1, c2, segmentT;
    if (t < splitPoint) {
      c1 = colors[0]; c2 = colors[1]; segmentT = t / splitPoint;
    } else {
      c1 = colors[1]; c2 = colors[2]; segmentT = (t - splitPoint) / (1 - splitPoint);
    }

    return `rgb(${Math.round(c1.r + (c2.r - c1.r) * segmentT)},${Math.round(c1.g + (c2.g - c1.g) * segmentT)},${Math.round(c1.b + (c2.b - c1.b) * segmentT)})`;
  };

  const CustomDot = ({ cx, cy, payload }) => (
    <circle
      cx={cx} cy={cy}
      r={payload.value !== null ? 8 : 6}
      fill={getPointColor(payload.value)}
      stroke="rgba(0,0,0,0.8)"
      strokeWidth={0}
      opacity={0.9}
      title={`Sensor ${payload.sensorIndex}: ${payload.value ?? 'No Data'}`}
    />
  );

  return (
    <div className="single-foot-container">
      <div className="heatmap-header">
        <h3>{footTitle} — {footView.toUpperCase()}</h3>
        <label className="max-control">
          Max:
          <input
            type="number"
            value={maxPressure}
            onChange={handleMaxChange}
            className="max-input"
            min="1"
            step="100"
          />
        </label>
      </div>

      <div
        className="foot-map"
        style={{
          backgroundImage: `url(${getFootImage(footView)})`,
          backgroundSize: 'cover',
          position: 'relative',
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
            <XAxis type="number" dataKey="x" hide domain={[transformedBounds.xMin, transformedBounds.xMax]} />
            <YAxis type="number" dataKey="y" hide domain={[transformedBounds.yMin, transformedBounds.yMax]} />
            <Scatter data={displayData} shape={<CustomDot />} />
          </ScatterChart>
        </ResponsiveContainer>
      </div>

      <div className="view-controls">
        <button
          className={`chart-dot ${footView === 'front' ? 'active' : ''}`}
          onClick={() => handleViewChange('front')}
          aria-label="Front View"
        />
        <button
          className={`chart-dot ${footView === 'back' ? 'active' : ''}`}
          onClick={() => handleViewChange('back')}
          aria-label="Back View"
        />
      </div>
      <div className="sensor-count">
        {displayData.length} sensors · {footView} view
      </div>
    </div>
  );
}

export default Heatmap;
